import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/auth/AuthContext";
import AuthCallback from "@/auth/AuthCallback";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import Home from "@/pages/Home";
import Application from "@/pages/Application";
import Legal from "@/pages/Legal";
import Login from "@/pages/Login";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import XePortal from "@/pages/xe/XePortal";
import PortalDashboard from "@/pages/portal/Dashboard";
import PortalReservations from "@/pages/portal/Reservations";
import PortalSettlements from "@/pages/portal/Settlements";
import PortalContracts from "@/pages/portal/Contracts";
import PortalPayouts from "@/pages/portal/Payouts";
import PortalPricing from "@/pages/portal/Pricing";
import PortalUpsells from "@/pages/portal/Upsells";
import Checkout from "@/pages/Checkout";
import InvestorLanding from "@/pages/investor/InvestorLanding";
import RequestAccess from "@/pages/investor/RequestAccess";
import InvestorDashboard from "@/pages/investor/InvestorDashboard";
import VehicleDetail from "@/pages/investor/VehicleDetail";
import InvestorCalculator from "@/pages/investor/Calculator";
import DataRoom from "@/pages/investor/DataRoom";
import InvestorStatements from "@/pages/investor/Statements";
import InvestorAdmin from "@/pages/xe/InvestorAdmin";
import PipelineApply from "@/pages/pipeline/PipelineApply";
import PipelinePortal from "@/pages/pipeline/PipelinePortal";
import AdminPipeline from "@/pages/xe/AdminPipeline";

const ScrollManager = () => {
  const location = useLocation();
  useEffect(() => {
    const lenis = window.__lenis;
    if (location.hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) {
          if (lenis) lenis.scrollTo(el, { offset: -88, duration: 1.4 });
          else el.scrollIntoView();
        }
      }, 120);
      return () => clearTimeout(t);
    }
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  return null;
};

const useSEO = () => {
  const location = useLocation();
  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]');
    const map = {
      "/": [
        "Xtreme Exotics Fleet Partner Program | Jet Car Business Opportunity",
        "Become an Xtreme Exotics Fleet Partner. Add a premium Jet Car attraction to your waterfront rental operation — no Jet Car purchase required. Performance-based weekly revenue share.",
      ],
      "/fleet-partner-application": [
        "Fleet Partner Application | Xtreme Exotics Watersports LLC",
        "Apply to become an Xtreme Exotics Fleet Partner. Tell us about your waterfront operation and our team will review your qualifications.",
      ],
      "/privacy-policy": [
        "Privacy Policy | Xtreme Exotics Watersports LLC",
        "How Xtreme Exotics Watersports LLC collects, uses and protects information submitted through the Fleet Partner Program.",
      ],
      "/terms-and-conditions": [
        "Terms and Conditions | Xtreme Exotics Watersports LLC",
        "Terms governing use of the Xtreme Exotics Watersports website and Fleet Partner application.",
      ],
      "/accessibility": [
        "Accessibility | Xtreme Exotics Watersports LLC",
        "Our commitment to an accessible website experience for all visitors.",
      ],
      "/investors": [
        "Investor Portal | Xtreme Exotics Watersports LLC",
        "Confidential preliminary discussion materials for a proposed vehicle-specific Jet Car opportunity. No offer to sell or solicitation to buy securities. No funds are being accepted.",
      ],
    };
    const [title, desc] = map[location.pathname] || map["/"];
    document.title = title;
    if (meta) meta.setAttribute("content", desc);
  }, [location.pathname]);
  return null;
};

const AppRouter = () => {
  const location = useLocation();
  // Detect OAuth session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/fleet-partner-application" element={<Application />} />
      <Route path="/login" element={<Login />} />
      <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><XePortal /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
      <Route path="/portal" element={<ProtectedRoute requireRole="partner"><PortalDashboard /></ProtectedRoute>} />
      <Route path="/portal/reservations" element={<ProtectedRoute requireRole="partner"><PortalReservations /></ProtectedRoute>} />
      <Route path="/portal/settlements" element={<ProtectedRoute requireRole="partner"><PortalSettlements /></ProtectedRoute>} />
      <Route path="/portal/contracts" element={<ProtectedRoute requireRole="partner"><PortalContracts /></ProtectedRoute>} />
      <Route path="/portal/payouts" element={<ProtectedRoute requireRole="partner"><PortalPayouts /></ProtectedRoute>} />
      <Route path="/portal/pricing" element={<ProtectedRoute requireRole="partner"><PortalPricing /></ProtectedRoute>} />
      <Route path="/portal/upsells" element={<ProtectedRoute requireRole="partner"><PortalUpsells /></ProtectedRoute>} />
      <Route path="/checkout/:reservationId" element={<Checkout />} />
      <Route path="/investors" element={<InvestorLanding />} />
      <Route path="/investors/request-access" element={<RequestAccess />} />
      <Route path="/investors/login" element={<Login investor />} />
      <Route path="/investors/portal" element={<ProtectedRoute><InvestorDashboard /></ProtectedRoute>} />
      <Route path="/investors/portal/vehicles/:vehicleId" element={<ProtectedRoute><VehicleDetail /></ProtectedRoute>} />
      <Route path="/investors/portal/calculator" element={<ProtectedRoute><InvestorCalculator /></ProtectedRoute>} />
      <Route path="/investors/portal/documents" element={<ProtectedRoute><DataRoom /></ProtectedRoute>} />
      <Route path="/investors/portal/statements" element={<ProtectedRoute><InvestorStatements /></ProtectedRoute>} />
      <Route path="/admin/investors" element={<ProtectedRoute adminOnly><InvestorAdmin /></ProtectedRoute>} />
      <Route path="/pipeline/apply" element={<PipelineApply />} />
      <Route path="/pipeline/portal" element={<ProtectedRoute><PipelinePortal /></ProtectedRoute>} />
      <Route path="/admin/pipeline" element={<ProtectedRoute adminOnly><AdminPipeline /></ProtectedRoute>} />
      <Route path="/privacy-policy" element={<Legal doc="privacy" />} />
      <Route path="/terms-and-conditions" element={<Legal doc="terms" />} />
      <Route path="/accessibility" element={<Legal doc="accessibility" />} />
      <Route path="*" element={<Home />} />
    </Routes>
  );
};

function App() {
  const lenisRef = useRef(null);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App bg-ink min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <ScrollManager />
          <SEOInner />
          <AppRouter />
          <Toaster position="top-center" theme="light" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

// Hooks must run inside Router context
function SEOInner() {
  useSEO();
  return null;
}

export default App;
